import EventsLayout from '@/components/layouts/EventLayout'
import TabbedPage from '@/components/shared/TabbedPage'
import { Button } from '@/components/ui'
import { MessageOutlined, PicCenterOutlined, PlusCircleFilled } from '@ant-design/icons'
import { usePage, router, Head } from '@inertiajs/react'
import { CustomCampaignsTab } from './components/CustomCampaignsTab'
import { PresetMessagesTab } from './components/PresetMessagesTab'
import { type Event, type WhatsappCampaign, type WhatsappTemplate, type AutomatedWhatsappTemplate } from './types'

type PageProps = {
  event: Event
  customTemplates: WhatsappTemplate[]
  campaigns: WhatsappCampaign[]
  presets: AutomatedWhatsappTemplate[]
  templatesError?: string | null
}

const titleize = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

// The multi-date reminders carry a date range ({{3}} – {{4}}) instead of a single
// day, for events that run across more than one date.
const TEMPLATE_LABELS: Record<string, string> = {
  registration_confirmation: 'Registration Confirmation',
  registration_acknowledgement: 'Registration Acknowledgement',
  reminder_1_hour: 'Reminder — 1 Hour Before',
  reminder_1_day: 'Reminder — 1 Day Before',
  reminder_1_week: 'Reminder — 1 Week Before',
  reminder_1_hour_multi_dates: 'Reminder — 1 Hour Before (Multi-Date Event)',
  reminder_1_day_multi_dates: 'Reminder — 1 Day Before (Multi-Date Event)',
  reminder_1_week_multi_dates: 'Reminder — 1 Week Before (Multi-Date Event)',
}

const labelFor = (templateType: string) =>
  TEMPLATE_LABELS[templateType] ?? titleize(templateType)

const getTabFromUrl = (): string => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    return params.get('tab') || 'preset'
  }
  return 'preset'
}

const WhatsappBlastsIndex = () => {
  const { event, customTemplates = [], campaigns = [], presets = [], templatesError } = usePage<PageProps>().props

  // Map backend presets to the shape PresetMessagesTab expects
  const presetTemplates = presets.map((p) => ({
    key: String(p.id),
    templateName: labelFor(p.templateType),
    enabled: p.enabled,
  }))

  // A single-date reminder and its multi-date twin fire at the same offset, so having
  // both on sends every attendee the same reminder twice.
  const enabledTypes = new Set(presets.filter((p) => p.enabled).map((p) => p.templateType))
  const conflictingPairs = presets
    .filter(
      (p) =>
        p.enabled &&
        p.multiDateCounterpart &&
        enabledTypes.has(p.multiDateCounterpart) &&
        p.templateType < p.multiDateCounterpart,
    )
    .map((p) => `${labelFor(p.templateType)} + ${labelFor(p.multiDateCounterpart as string)}`)

  const handleTogglePreset = (updatedTemplates: { key: string; templateName: string; enabled: boolean }[]) => {
    // Find which template changed by comparing with current presets
    for (const updated of updatedTemplates) {
      const original = presets.find((p) => String(p.id) === updated.key)
      if (original && original.enabled !== updated.enabled) {
        router.patch(
          `/events/${event.slug}/messaging/whatsapp_blasts/preset/${updated.key}`,
          {
            automated_whatsapp_template: { enabled: updated.enabled },
          },
          { preserveScroll: true },
        )
        break
      }
    }
  }

  const handleCreateCampaign = () => {
    window.location.href = `/events/${event.slug}/messaging/whatsapp_blasts/new`
  }

  const tabs = [
    {
      key: 'preset',
      label: (
        <span className="flex items-center gap-2">
          <MessageOutlined />
          Preset Automated Messages
        </span>
      ),
      children: (
        <PresetMessagesTab
          presetTemplates={presetTemplates}
          eventSlug={event.slug}
          onTemplatesChange={handleTogglePreset}
          conflictingPairs={conflictingPairs}
        />
      ),
    },
    {
      key: 'custom',
      label: (
        <span className="flex items-center gap-2">
          <PicCenterOutlined />
          Custom Whatsapp Campaigns
        </span>
      ),
      children: (
        <CustomCampaignsTab
          campaigns={campaigns}
          eventSlug={event.slug}
        />
      ),
    },
  ]

  return (
    <EventsLayout>
      <Head title="WhatsApp Blast - Messaging - Events" />
      <TabbedPage
        title="Whatsapp Blast"
        subtitle="You may edit preconfigured templates or create custom templates via the Meta Business Dashboard."
        headerAction={
          <Button
            variant="primary"
            icon={<PlusCircleFilled />}
            onClick={handleCreateCampaign}
          >
            Create WhatsApp Campaign
          </Button>
        }
        tabs={tabs}
        defaultActiveKey={getTabFromUrl()}
        onTabChange={(key) => {
          const url = new URL(window.location.href)
          url.searchParams.set('tab', key)
          window.history.replaceState({}, '', url.toString())
        }}
      />
    </EventsLayout>
  )
}

export default WhatsappBlastsIndex
